import React from 'react';

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  label?: string;
  name?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = 'Selecione uma opção',
  required = false,
  disabled = false,
  error,
  helpText,
  size = 'md',
  fullWidth = true,
  icon: Icon,
  className = '',
}) => {
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-4 py-3.5 text-lg',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <div className={`${widthClass} ${className}`}>
      {label && (
        <label className="flex items-center gap-2 text-[#ccc] text-sm mb-2">
          {Icon && <Icon className="text-[#089453]" />}
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          className={`
            ${widthClass}
            ${sizeClasses[size]}
            bg-white/5 
            border 
            ${error ? 'border-red-500' : 'border-white/20'}
            rounded-lg 
            text-white 
            transition-all 
            duration-300 
            cursor-pointer
            focus:outline-none 
            focus:border-[#089453] 
            focus:bg-[#089453]/10
            disabled:opacity-50 
            disabled:cursor-not-allowed
            appearance-none
            pr-10
            [&>option]:bg-[#1a2332]
            [&>option]:text-white
            [&>option]:py-2
          `}
          style={{
            // Fallback para navegadores que não suportam [&>option]
            colorScheme: 'dark',
          }}
        >
          {placeholder && (
            <option value="" disabled style={{ backgroundColor: '#1a2332', color: '#888888' }}>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              style={{ backgroundColor: '#1a2332', color: '#ffffff' }}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Seta customizada */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className="w-5 h-5 text-[#888]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {helpText && !error && (
        <p className="mt-1 text-xs text-[#888]">{helpText}</p>
      )}

      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default Select;
